/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Model, { attr } from '@ember-data/model';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import {
  IMPORT_SOURCE_STATUSES,
  IMPORT_SOURCE_IN_PROGRESS_STATUSES,
} from 'embercom/lib/knowledge-hub/constants';
import { fragment } from 'ember-data-model-fragments/attributes';
import type ImportSourceContext from './import-source-context';

export interface LegacyCondition {
  key: string;
  operator: '==' | '!=';
  value: string;
}

export interface Condition {
  field_type: 'standard' | 'metafield';
  namespace?: string;
  key: string;
  operator: string;
  value?: string | null;
}

export interface MultiConditionRules {
  logical_operator: 'and' | 'or';
  conditions: Condition[];
}

export interface ShopifyMetafieldDefinition {
  name: string;
  namespace: string;
  key: string;
  type: string;
  description?: string;
}

export enum SourceType {
  Confluence = 0,
  Guru = 1,
  Notion = 2,
  Web = 3,
  Zendesk = 4,
  Salesforce = 6,
  Box = 8,
  Document360 = 9,
  Freshdesk = 10,
  Shopify = 11,
  HubSpot = 13,
}

export const SourceTypeName = {
  [SourceType.Confluence]: 'confluence',
  [SourceType.Guru]: 'guru',
  [SourceType.Notion]: 'notion',
  [SourceType.Web]: 'web',
  [SourceType.Zendesk]: 'zendesk',
  [SourceType.Salesforce]: 'salesforce_knowledge',
  [SourceType.Box]: 'box',
  [SourceType.Document360]: 'document360',
  [SourceType.Freshdesk]: 'freshdesk',
  [SourceType.Shopify]: 'shopify',
  [SourceType.HubSpot]: 'hubspot',
};

export enum SyncBehaviorType {
  Sync = 0,
  Import = 1,
  Diff_sync = 4, // this is used for selected entities sync
}

export enum PreferredAuthMethod {
  OAuth = 0,
  ApiKey = 1,
}

export default class ImportSource extends Model {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare notificationsService: any;
  @service declare intl: IntlService;

  @attr('number') declare sourceType: SourceType;
  @attr('number') declare lastSuccessfulJobId: number;
  @attr('string') declare lastSuccessfulJobDate: string;
  @attr('number') declare importedEntityCount: number;
  @attr('string') declare status: string;
  @attr('number') declare syncBehavior: SyncBehaviorType;
  @fragment('import-service/import-source-context') declare context: ImportSourceContext;
  @attr('boolean') declare hasCredentials: boolean;
  @attr('array', { defaultValue: () => [] }) declare fileTypes: string[];
  // TODO: Move the attributes below to the context fragment
  @attr('string') declare baseUrl: string;
  @attr('string') declare spaceId: string;
  @attr('array', { defaultValue: () => [] }) declare denyFolderIds: string[];

  // Confluence properties
  @attr('string') declare spaceName?: string;
  @attr('string') declare siteId?: string;
  @attr('boolean') declare useV2?: boolean;

  // Guru properties
  @attr('string') declare collectionId?: string;
  @attr('string') declare collectionName?: string;

  @attr('string') declare email: string;
  @attr('string') declare authToken: string;
  @attr('number', { defaultValue: PreferredAuthMethod.OAuth })
  declare preferredAuthMethod: PreferredAuthMethod;

  // Document360 properties
  @attr('string') declare projectId?: string;
  @attr('string') declare hostingRegion?: string;
  @attr('array', { defaultValue: () => [] }) declare languageCodes?: string[];
  @attr('string') declare projectName?: string;
  @attr('array', { defaultValue: () => [] }) declare selectedCategoryIds?: string[];

  // Box properties
  @attr('array') declare selectedItems: string[];

  // Freshdesk properties
  @attr('string') declare domain?: string;

  // Zendesk properties
  @attr('boolean') declare syncPrivateArticles?: boolean;
  @attr('boolean') declare syncStaffArticles?: boolean;
  @attr('string') declare helpCenterId?: string;

  // Shopify properties
  @attr('string') declare shopifyDomain?: string;
  @attr() declare outOfStockCondition?: MultiConditionRules | LegacyCondition | null;
  @attr('array') declare shopifyMetafieldDefinitions?: ShopifyMetafieldDefinition[];

  @attr('array') declare entityMetadata: any;
  @attr('array') declare metadata: any;
  @attr('number') declare remoteContentCount: number;
  @attr('string') declare remoteContentCountError: string;

  get isInProgress(): boolean {
    return IMPORT_SOURCE_IN_PROGRESS_STATUSES.includes(this.status);
  }

  get isFailed(): boolean {
    return this.status === IMPORT_SOURCE_STATUSES.failed;
  }

  get isUnauthorized(): boolean {
    return this.context?.unauthorized || false;
  }

  get isImport(): boolean {
    return this.syncBehavior === SyncBehaviorType.Import;
  }

  get isSync(): boolean {
    return this.syncBehavior === SyncBehaviorType.Sync;
  }

  get emptySource(): boolean {
    if (!!this.lastSuccessfulJobId || this.importedEntityCount > 0) {
      return false;
    }

    switch (this.sourceType) {
      case SourceType.Confluence:
        return !this.siteId && !this.spaceId;
      case SourceType.Guru:
        return !this.context?.collectionName;
      case SourceType.Notion:
        return !this.context?.workspaceName;
      case SourceType.Box:
        return !this.context?.selectedItems?.length;
      case SourceType.Document360:
        return !this.projectId || !this.hostingRegion || !this.languageCodes?.length;
      case SourceType.Salesforce:
        return !this.hasCredentials;
      case SourceType.Freshdesk:
        return !this.hasCredentials;
      case SourceType.Shopify:
        return !this.shopifyDomain;
    }
    return true;
  }

  get sourceName(): string {
    switch (this.sourceType) {
      case SourceType.Confluence:
        return 'Confluence space'; // eslint-disable-line @intercom/intercom/no-bare-strings
      case SourceType.Guru:
        return 'Guru collection'; // eslint-disable-line @intercom/intercom/no-bare-strings
      case SourceType.Notion:
        return 'Notion space'; // eslint-disable-line @intercom/intercom/no-bare-strings
      case SourceType.Box:
        return 'Box'; // eslint-disable-line @intercom/intercom/no-bare-strings
      case SourceType.Document360:
        return 'Document360'; // eslint-disable-line @intercom/intercom/no-bare-strings
      case SourceType.Freshdesk:
        return 'Freshdesk'; // eslint-disable-line @intercom/intercom/no-bare-strings
      case SourceType.Shopify:
        return 'Shopify'; // eslint-disable-line @intercom/intercom/no-bare-strings
    }
    return '';
  }

  // TODO: Move this to the context fragment after moving the attributes there
  get name(): string {
    return (
      this.customSourceName ||
      this.spaceName ||
      this.collectionName ||
      this.context?.workspaceName ||
      this.context?.domain
    );
  }

  get importConfig() {
    return this.context?.import_config || {};
  }

  get autoEnableChatbot(): boolean {
    return this.importConfig.auto_enable_chatbot ?? false;
  }

  get autoEnableCopilot(): boolean {
    return this.importConfig.auto_enable_copilot ?? false;
  }

  get metadataFields(): string[] {
    return this.importConfig.metadata_fields ?? [];
  }

  get prefilterRules(): {
    type: string;
    predicates: Array<{ descriptor_name: string; comparison: string; value: any }>;
  } | null {
    return this.importConfig.prefilter_rules ?? null;
  }

  get customSourceName(): string | undefined {
    return this.importConfig.custom_name;
  }

  get isConversationSyncingEnabled(): boolean {
    if (this.context?.manually_paused === true) {
      return false;
    }
    return true;
  }

  @action async triggerResync() {
    try {
      await this.knowledgeHubService.startImport(this.id);
    } catch (error) {
      if (error.jqXHR.status === 409) {
        this.notificationsService.notifyWarning(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-already-running'),
        );
        return;
      }
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
      );
    }
  }

  @action async triggerImport({ notify = true } = {}) {
    try {
      await this.knowledgeHubService.import(this.id);
      if (notify) {
        this.knowledgeHubService.notifySourceStateChangeConfirmation(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.import-source-processing'),
          `${this.id}`,
        );
      }
    } catch (error) {
      if (error.jqXHR.status === 409) {
        this.notificationsService.notifyWarning(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-already-running'),
        );
        return;
      }
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.sources.actions.import-source-error'),
      );
    }
  }

  @action async remove({ notify = true } = {}) {
    try {
      await this.knowledgeHubService.removeImportSource(this);
      if (notify) {
        this.knowledgeHubService.notifySourceStateChangeConfirmation(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.remove-done'),
          `${this.id}`,
        );
      }
    } catch (error) {
      if (notify) {
        let errorMessage =
          error.jqXHR.status === 409
            ? this.intl.t('knowledge-hub.filterable-list.sources.actions.delete-conflict-error')
            : this.intl.t('knowledge-hub.filterable-list.sources.actions.remove-error');
        this.notificationsService.notifyError(errorMessage);
      }
    }
  }
}
